import { useState } from 'react';
import { CopyIcon, CheckIcon, LinkIcon, Loader2Icon } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import BackofficePage, {
    DataCard,
    DataTable,
    TH,
    TR,
    TD,
} from '../../components/BackofficePage';
import {
    buildUploadLink,
    useCreateUploadSession,
    useRevokeUploadSession,
    useUploadSessions,
    type CreatedUploadSession,
    type UploadSessionView,
} from '@/services/uploadSession.service';

const EXPIRY_OPTIONS = [2, 6, 24];

function formatBytes(bytes: number): string {
    if (bytes >= 1024 * 1024 * 1024)
        return `${(bytes / (1024 * 1024 * 1024)).toFixed(1)} GB`;
    if (bytes >= 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(0)} MB`;
    if (bytes >= 1024) return `${(bytes / 1024).toFixed(0)} KB`;
    return `${bytes} B`;
}

function StatusBadge({ session }: { session: UploadSessionView }) {
    const { label, className } = session.revoked
        ? { label: 'Revoked', className: 'bg-red-900/30 text-red-300' }
        : session.expired
          ? { label: 'Expired', className: 'bg-[#2a2118] text-amber-300' }
          : { label: 'Active', className: 'bg-[#16271d] text-[#74c69d]' };
    return (
        <span
            className={`inline-block rounded px-2 py-0.5 text-[11px] font-medium ${className}`}
        >
            {label}
        </span>
    );
}

export default function BackOfficeTransfer() {
    const { data: sessions, isLoading } = useUploadSessions();
    const { mutate: createSession, isPending: creating } =
        useCreateUploadSession();
    const { mutate: revokeSession } = useRevokeUploadSession();

    const [label, setLabel] = useState('');
    const [expiryHours, setExpiryHours] = useState(6);
    const [capGb, setCapGb] = useState(20);
    const [created, setCreated] = useState<CreatedUploadSession | null>(null);
    const [copied, setCopied] = useState(false);

    const handleCreate = () => {
        if (!label.trim()) return;
        createSession(
            { label: label.trim(), expiryHours, capGb },
            {
                onSuccess: (session) => {
                    setCreated(session);
                    setCopied(false);
                    setLabel('');
                },
            },
        );
    };

    const handleCopy = (token: string) => {
        navigator.clipboard.writeText(buildUploadLink(token));
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };

    const handleRevoke = (session: UploadSessionView) => {
        if (window.confirm(`Revoke the link "${session.label}"?`))
            revokeSession(session.id);
    };

    return (
        <BackofficePage
            title="Transfer links"
            description="Generate a time-limited link so a customer can drop hub files (json / jpg / jp2) straight into the transfer bucket. You then move them to prod manually."
        >
            {/* Create form */}
            <DataCard>
                <div className="p-5 space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-[1fr_auto_auto_auto] gap-3 items-end">
                        <div>
                            <label className="block text-[11px] uppercase tracking-wider text-[#6b7480] mb-1.5">
                                Label (customer / project)
                            </label>
                            <Input
                                value={label}
                                onChange={(e) => setLabel(e.target.value)}
                                placeholder="e.g. INRAE — Project Maize 2026"
                                className="bg-[#0f141a] border-[#1e242a] text-[#e5e7e3]"
                            />
                        </div>
                        <div>
                            <label className="block text-[11px] uppercase tracking-wider text-[#6b7480] mb-1.5">
                                Expires in
                            </label>
                            <select
                                value={expiryHours}
                                onChange={(e) =>
                                    setExpiryHours(Number(e.target.value))
                                }
                                className="h-10 rounded-md bg-[#0f141a] border border-[#1e242a] text-[#e5e7e3] px-3 text-sm"
                            >
                                {EXPIRY_OPTIONS.map((h) => (
                                    <option key={h} value={h}>
                                        {h} hours
                                    </option>
                                ))}
                            </select>
                        </div>
                        <div>
                            <label className="block text-[11px] uppercase tracking-wider text-[#6b7480] mb-1.5">
                                Size cap (GB)
                            </label>
                            <Input
                                type="number"
                                min={1}
                                max={1024}
                                value={capGb}
                                onChange={(e) =>
                                    setCapGb(Number(e.target.value))
                                }
                                className="w-28 bg-[#0f141a] border-[#1e242a] text-[#e5e7e3]"
                            />
                        </div>
                        <Button
                            onClick={handleCreate}
                            disabled={creating || !label.trim()}
                            className="bg-[#2f6f4e] hover:bg-[#3a865f] text-white"
                        >
                            {creating ? (
                                <Loader2Icon className="h-4 w-4 animate-spin" />
                            ) : (
                                <LinkIcon className="h-4 w-4" />
                            )}
                            Generate link
                        </Button>
                    </div>

                    {created && (
                        <div className="rounded-md border border-[#2f6f4e]/50 bg-[#13231b] p-4">
                            <div className="text-[11px] uppercase tracking-wider text-[#74c69d] mb-2">
                                Link ready — share it with the uploader
                            </div>
                            <div className="flex items-center gap-2">
                                <code className="flex-1 truncate rounded bg-[#0a0e12] px-3 py-2 text-xs text-[#d8dde2] font-mono">
                                    {buildUploadLink(created.token)}
                                </code>
                                <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => handleCopy(created.token)}
                                    className="bg-[#0f141a] border-[#1e242a] text-[#d8dde2] hover:bg-[#1a2126] hover:text-white"
                                >
                                    {copied ? (
                                        <CheckIcon className="h-3.5 w-3.5 text-[#74c69d]" />
                                    ) : (
                                        <CopyIcon className="h-3.5 w-3.5" />
                                    )}
                                    {copied ? 'Copied' : 'Copy'}
                                </Button>
                            </div>
                            <p className="mt-2 text-xs text-[#a8b0bb]">
                                Valid until{' '}
                                {new Date(created.expiresAt).toLocaleString()} ·
                                up to {created.capGb} GB. The token is shown only
                                here — copy it now.
                            </p>
                        </div>
                    )}
                </div>
            </DataCard>

            {/* Existing sessions */}
            <div className="mt-6">
                {isLoading ? (
                    <Skeleton className="w-full h-[300px] bg-[#1e242a]" />
                ) : (
                    <DataCard>
                        <DataTable>
                            <thead className="bg-[#0f141a]">
                                <tr>
                                    <TH>Label</TH>
                                    <TH align="center">Status</TH>
                                    <TH align="center">Used / cap</TH>
                                    <TH align="center">Expires</TH>
                                    <TH align="center">Created</TH>
                                    <TH align="center"> </TH>
                                </tr>
                            </thead>
                            <tbody>
                                {sessions?.length ? (
                                    sessions.map((s) => (
                                        <TR key={s.id}>
                                            <TD>{s.label}</TD>
                                            <TD align="center">
                                                <StatusBadge session={s} />
                                            </TD>
                                            <TD align="center" className="text-xs">
                                                {formatBytes(s.uploadedBytes)} /{' '}
                                                {s.capGb} GB
                                            </TD>
                                            <TD align="center" className="text-xs">
                                                {new Date(
                                                    s.expiresAt,
                                                ).toLocaleString()}
                                            </TD>
                                            <TD align="center" className="text-xs">
                                                {new Date(
                                                    s.createdAt,
                                                ).toLocaleString()}
                                            </TD>
                                            <TD align="center">
                                                {s.active && (
                                                    <Button
                                                        variant="ghost"
                                                        size="sm"
                                                        onClick={() =>
                                                            handleRevoke(s)
                                                        }
                                                        className="text-[#a8b0bb] hover:text-red-400 hover:bg-red-900/20"
                                                    >
                                                        Revoke
                                                    </Button>
                                                )}
                                            </TD>
                                        </TR>
                                    ))
                                ) : (
                                    <TR>
                                        <TD
                                            align="center"
                                            className="text-[#6b7480]"
                                        >
                                            <span />
                                        </TD>
                                        <TD align="center"> </TD>
                                        <TD
                                            align="center"
                                            className="text-[#6b7480]"
                                        >
                                            No links yet
                                        </TD>
                                        <TD align="center"> </TD>
                                        <TD align="center"> </TD>
                                        <TD align="center"> </TD>
                                    </TR>
                                )}
                            </tbody>
                        </DataTable>
                    </DataCard>
                )}
            </div>
        </BackofficePage>
    );
}
